import React, {useEffect, useMemo, useState} from "react"
import { DataTable } from "@/components/ui/DataTable"
import { getBillsColumns } from "./billsColumns"
import { Bill, BillAction } from '@/types/bill'
import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader2 } from "lucide-react"
import { BillSearchInput } from "@/components/BillSearchInput"
import { SortField, SortDirection } from '@/hooks/bills/useBillsWorkflow'
import { BillCard } from "@/components/BillCard"
import { BulkActionBar } from "@/components/BulkActionBar"
import { useToast } from "@/hooks/use-toast.ts"
import { BulkPrintDialog } from "./printing/BulkPrintDialog"

interface BillsTableProps {
  bills: Bill[]
  selectedBills: string[]
  onSelectBill: (billId: string) => void
  onSelectAll: (bills: Bill[]) => void
  onProcessAction: (billId: string, action: BillAction, allBillIds?: string[]) => void
  onShowImpactDetails?: (bill: Bill) => void
  currentPage: number
  pageSize: number
  totalPages: number
  onPageChange: (page: number) => void
  searchQuery: string
  onSearchChange: (value: string) => void
  isLoading: boolean  
  processingActions: Record<string, BillAction>
  sortField?: SortField
  sortDirection?: SortDirection
  onSortChange?: (field: SortField, direction: SortDirection) => void
  session?: string | null
  totalFilteredItems: number
}

export function BillsTable({
  bills = [],
  selectedBills = [],
  onSelectBill,
  onSelectAll,
  onProcessAction,
  onShowImpactDetails,
  currentPage,
  pageSize,
  totalPages,
  onPageChange,
  searchQuery,
  onSearchChange,
  isLoading,
  processingActions,
  sortField = null,
  sortDirection = 'desc',
  onSortChange,
  session,
  totalFilteredItems
}: BillsTableProps) {
  const [isMobile, setIsMobile] = useState(false)
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false)
  const { toast } = useToast()

  // Session code from BillsPage (fallback to empty string if null)
  const sessionCode = session ?? ""

  // Update mobile state on resize
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768) // 768px is the md breakpoint in Tailwind
    }

    checkIfMobile() // Check initially

    window.addEventListener('resize', checkIfMobile)
    return () => window.removeEventListener('resize', checkIfMobile)
  }, [])

  // IMPORTANT: Don't filter the bills here, just use them directly from props
  // The backend API is already filtering by workflow_status
  const filteredBills = useMemo(() => {
    console.log("BillsTable received bills:", bills.length, "items"); 
    return bills;
  }, [bills])

  const validBills = filteredBills.filter(
    (bill): bill is Bill =>
      bill !== null && bill !== undefined && typeof bill.billNumber === 'string'
  )

  const getSelectedBills = (): Bill[] => {
    return validBills.filter(bill => selectedBills.includes(bill.billNumber));
  }
  
  const handleOpenBulkPrintDialog = () => {
    if (selectedBills.length === 0) {
      toast({
        title: "No bills selected",
        description: "Please select at least one bill to print.",
        variant: "destructive"
      })
      return;
    }
    
    setIsPrintDialogOpen(true);
  }

  const handlePDFClick = (url: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  // Pass props to getBillsColumns for table functionality
  const columns = getBillsColumns({
    selectedBills,
    onSelectBill,
    onSelectAll,
    onProcessAction,
    onShowImpactDetails,
    handlePDFClick,
    processingActions,
    sortField,
    sortDirection,
    onSortChange,
    selectedSession: sessionCode,
  })

  const startItem = (currentPage - 1) * pageSize + 1
  const endItem = Math.min(
    currentPage * pageSize,
    (currentPage - 1) * pageSize + validBills.length
  )
  const totalItems = totalFilteredItems

  return (
    <div className="space-y-4 border rounded-md relative">
      <div className="p-3 border-b">
        <div className="flex flex-col sm:flex-row gap-3 justify-between items-stretch sm:items-center">
          <div className="w-full sm:w-auto order-2 sm:order-1">
            <BillSearchInput
              value={searchQuery}
              onChange={onSearchChange}
              placeholder="Search by Bill Number"
            />
          </div>
          <div className="w-full sm:w-auto order-1 sm:order-2">
            <BulkActionBar
              selectedCount={selectedBills.length}
              isProcessing={isBulkProcessing}
              onAction={(action) => {
                if (selectedBills.length > 0) {
                  setIsBulkProcessing(true)
                  Promise.resolve(
                    onProcessAction(selectedBills[0], action, selectedBills)
                  ).finally(() => setIsBulkProcessing(false))
                }
              }}
              onOpenBulkPrintDialog={handleOpenBulkPrintDialog}
              allSelectedHaveReviews={
                selectedBills.length > 0 &&
                selectedBills.every((billNumber) => {
                  const bill = validBills.find((b) => b.billNumber === billNumber)
                  if (!bill || !bill.versions || bill.versions.length === 0)
                    return false

                  // Check if any version has a legislative review
                  return bill.versions.some(
                    (version) => version.legislativeReviewS3Key
                  )
                })
              }
            />
          </div>
        </div>
      </div>

      {isMobile ? (
        // Mobile view -> use BillCard
        <div className="p-4 grid grid-cols-1 gap-4">
          {validBills.map((bill) => (
            <BillCard
              key={bill.billNumber}
              bill={bill}
              isSelected={selectedBills.includes(bill.billNumber)}
              sessionCode={sessionCode}
              onSelectBill={onSelectBill}
              onProcessAction={onProcessAction}
              onShowImpactDetails={onShowImpactDetails}
              handlePDFClick={handlePDFClick}
              processingActions={processingActions}
            />
          ))}
        </div>
      ) : (
        // Desktop view -> use DataTable
        <DataTable<Bill>
          columns={columns}
          data={validBills}
          isLoading={isLoading}
        />
      )}
      
      {/* BulkPrintDialog for printing multiple reviews */}
      <BulkPrintDialog
        bills={getSelectedBills()}
        open={isPrintDialogOpen}
        onOpenChange={setIsPrintDialogOpen}
      />

      {/* Always show the loading indicator when loading, even if there are bills */}
      {isLoading && (
        <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
          <div className="flex items-center gap-2 p-2 bg-background/90 rounded shadow">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading bills...</span>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 py-2 border-t gap-4">
        <div className="text-sm text-muted-foreground">
          {isLoading 
            ? 'Loading bills...'
            : totalItems === 0
              ? 'No bills found'
              : `Showing ${startItem} to ${endItem} of ${totalItems} bills`}
          {searchQuery && !isLoading && totalItems > 0 && ` (filtered)`}
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto overflow-x-auto pb-1">
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1 || isLoading}
              aria-label="Go to first page"
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                if (currentPage > 1) {
                  onPageChange(currentPage - 1)
                }
              }}
              disabled={currentPage === 1 || isLoading}
              aria-label="Go to previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            {/* Page numbers */}
            <div className="flex items-center gap-1 mx-2 overflow-x-auto">
              {generatePageNumbers(currentPage, totalPages).map((pageNum, idx) =>
                pageNum === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2" aria-hidden="true">
                    ...
                  </span>
                ) : (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      onPageChange(pageNum)
                    }}
                    disabled={isLoading}
                    className="min-w-[2rem] flex-shrink-0"
                    aria-label={`Go to page ${pageNum}`}
                    aria-current={currentPage === pageNum ? 'page' : undefined}
                  >
                    {pageNum}
                  </Button>
                )
              )}
            </div>

            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                if (currentPage < totalPages) {
                  onPageChange(currentPage + 1)
                }
              }}
              disabled={currentPage === totalPages || isLoading}
              aria-label="Go to next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages || isLoading}
              aria-label="Go to last page"
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper function to generate page numbers with ellipsis
function generatePageNumbers(current: number, total: number): (number | '...')[] {
  const pages: (number | '...')[] = []
  const maxVisiblePages = 7 // total number of page buttons to show

  if (total <= maxVisiblePages) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  pages.push(1)
  if (current > 3) pages.push('...')

  for (
    let i = Math.max(2, current - 1);
    i <= Math.min(total - 1, current + 1);
    i++
  ) {
    pages.push(i)
  }

  if (current < total - 2) pages.push('...')
  if (total > 1) pages.push(total)

  return pages
}